import { useState, useEffect, useMemo } from "react";
import { Typography, Modal, Menu, Alert, Checkbox, Tooltip } from "antd";
import {DataStore} from '@reams/elias-store';
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { WarningOutlined } from "@ant-design/icons";
import { EditIcon, DeleteIcon, CopyIcon, MoveIcon } from "components/Icons";
import MoveAssetDrawer from "pages/SpaceDetailsPage/MoveAssetDrawer/MoveAssetDrawer";
import ItemDrawer from "components/ItemDrawer/ItemDrawer";
import FilterHeader from "components/FilterHeader/FilterHeader";
import { Space, Asset } from "models";
import Spinner from "components/Spinner/Spinner";
import CopyAssetPane from "./CopyAssetPane/CopyAssetPane";
import ItemTableGrid from "components/ItemTableGrid/ItemTableGrid";
import CopyAssetCompleteMessage from "./CopyAssetPane/CopyAssetCompleteMessage";
import { saveFavouriteSpaces } from "helpers/favouriteSpaceHelpers";
import FabricGroups from "components/FabricGroups/FabricGroups";
import Authorize from "components/Authorize/Authorize";
import { HiddenActionsInDropdown } from "components/ButtonPair/ActionsDropdown";
import {
  ASSET_COLUMN,
  useRenderGrid,
} from "../../helpers/asset/assetTableHelpers";
import _ from "lodash";
import "../../styles/customLinks.scss";
import {
  buildFilterTreeFromHierarchy,
  buildFilterableTreeByFacet,
  filterableOptions,
  treeObject,
  getEnhancedValidationStatus,
} from "helpers/filters/assetFilterHelpers";
import {
  fetchFilterDescription,
  filterItem,
} from "helpers/filters/itemFilterHelpers";
import { useLocation } from "react-router-dom";
import { useEntityData } from "EntityDataContext/EntityDataContext";
import { getSessionStorage, setSessionStorage, getLocalStorage, setLocalStorage } from "helpers/browserStorage";
import { getAssetDefinition } from "helpers/hierarchyHelpers";
import CopyAssetDrawer from "./CopyAssetDrawer/CopyAssetDrawer";
import CopyAssetsFromSpaceDrawer from "./CopyAssetsFromSpaceDrawer/CopyAssetsFromSpaceDrawer";
import { logUserEvent } from "helpers/userEventLogger";
import { useOnlineContext } from "helpers/contexts/OnlineContext/OnlineContext";

export default function SpaceDetailsPage({
  pageHeader,
  addPageToHistory,
}) {
  const PAGE_KEY = "AssetsListing";
  const DEFAULT_SORT = "updatedAt";
  const DEFAULT_SORT_ORDER = "descend";

  const hierarchy = window.hierarchy;
  const {
    favouriteSpacesState,
    copyAssets,
    floors,
    spaces,
    assets: assetData,
    facilities,
    deletedSpaces,
    setDeletedSpaces,
    showCopyDrawer,
    setShowCopyDrawer,
    assetsToCopyList,
    setAssetsToCopyList,
    userData: { email },
    tenantData: { showAssetValidationStatus = false },
    syncStatusHook: { imagesToUploadCount }
  } = useEntityData()
  
  const { isOnline } = useOnlineContext()

  const [sortBy, setSortBy] = useState(getLocalStorage(PAGE_KEY, "SortBy") || DEFAULT_SORT);
  const [sortOrder, setSortOrder] = useState(getLocalStorage(PAGE_KEY, "SortOrder") || DEFAULT_SORT_ORDER);
  const [copyList, setCopyList] = copyAssets;
  const [favouriteSpaces, setFavouriteSpaces] = favouriteSpacesState;
  const [isCreatingNewAsset, setIsCreatingNewAsset] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState();
  const [copyAssetMessage, setCopyAssetMessage] = useState();
  const [movingAsset, setMovingAsset] = useState();
  const [copyAsset, setCopyAsset] = useState();
  const [movingAssetComplete, setMovingAssetComplete] = useState();
  const [editSpace, setEditSpace] = useState(false);
  const [showCopyFromSpaceDrawer, setShowCopyFromSpaceDrawer] = useState(false);
  const [filters, setFilters] = useState([]);
  const { spaceId } = useParams();
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const currentSessionFilters = getSessionStorage(PAGE_KEY, "Filters") || [];

    if (currentSessionFilters.length && !filters.length) {
      currentSessionFilters.map((f) => setFiltersNode(f));
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const sessionSortBy = getLocalStorage(PAGE_KEY, "SortBy");
    const sessionSortOrder = getLocalStorage(PAGE_KEY, "SortOrder");

    if (sortBy !== sessionSortBy) {
      setLocalStorage(PAGE_KEY, "SortBy", sortBy);
    }

    if (sortOrder !== sessionSortOrder) {
      setLocalStorage(PAGE_KEY, "SortOrder", sortOrder);
    }
  }, [sortBy, sortOrder])
  
  useEffect(() => {
    setSelectedAsset(location?.state?.asset);
  }, [location]);

  useEffect(() => {
    if (spaces.items?.[spaceId]?.name) {
      addPageToHistory(spaces.items[spaceId].name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId]);

  const space = spaces.items?.[spaceId]

  if (!space || deletedSpaces.indexOf(space.id) > -1) {
    navigate('/')
  }
  
  const facility = useMemo(() => {
    if (!space) return undefined

    return facilities?.[space.facilityId]
  }, [space, facilities])

  const floor = useMemo(() => {
    if (!space) return undefined

    return floors.items?.[space.floorId]
  }, [space, floors])


  const assets = useMemo(() => {
    if (!space || !floor || !facility) return []

    return (assetData?.assetTree?.[space.facilityId]?.[space.floorId]?.[space.id] || []).map(assetId => assetData.items[assetId])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetData, spaceId, space, floor, facility])

  const setFiltersNode = (val) => {
    let desc = val;

    if (desc !== 'validation-status_Not Validated') {
      desc = fetchFilterDescription({filter: desc, facets: window.facets});
    } else {
      desc = 'Validation Status: Not Validated';
    }

    const x = { id: val, description: desc };

    setFilters((old) => {
      if (old.find((o) => o.id === x.id)) {
        return [...old];
      }
      return [...old, x];
    });
  };

  const onFiltersChange = (node) => {
    setSessionStorage(PAGE_KEY, "Filters", node);

    // from select fields
    if (Array.isArray(node)) {
      node.map((node) => setFiltersNode(node));
      return;
    }

    // from option fields
    setFiltersNode(node.value);
  };

  const onFilterClose = (filter) => {
    const newFilterKeys = (getSessionStorage(PAGE_KEY, "Filters") || []).filter(f => f !== filter);

    setSessionStorage(PAGE_KEY, "Filters", newFilterKeys);

    setFilters((old) => {
      return old.filter((f) => f.id !== filter);
    });
  };

  const enhancedAssets = useMemo(() => {
    let tmpAssets = assets?.map((asset) => {

      const assetDefinition = getAssetDefinition(
        asset.assetType?.eliasID,
        hierarchy
        );

      return {
        ...asset,
        name: assetDefinition?.knownAs || asset.assetType?.description,
        barcode: `${asset.facets?.barcode || ''} ${asset.notes?.barcode?.status ? `[${asset.notes?.barcode?.status}]` : ''}` ,
        quantity: asset.facets?.quantity,
        installDate: asset.facets && asset.facets["install-date"],
        condition: asset.facets?.condition,
        manufacturer: asset.facets?.manufacturer,
      };
    });
    
    return filterItem(tmpAssets, filters, window.facets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, filters]);
  
  const fabricAssets = assets.filter(a => a.assetType?.fabricElement)

  const actions = (asset) => {
    return [
      {
        label: "Edit",
        key: "edit",
        onClick: (e) => {
          const { id } = asset

          logUserEvent({
            user: email,
            eventType: `Edit asset ${id} clicked on asset listing via Space Details view`,
            imageOutboxSize: imagesToUploadCount
          }, isOnline);

          setSelectedAsset(asset);
        },
        icon: <EditIcon />,
      },
      {
        label: "Copy",
        key: "copy",
        onClick: () => {
          const { id } = asset

          logUserEvent({
            user: email,
            eventType: `Copy asset ${id} clicked on asset listing via Space Details view`,
            imageOutboxSize: imagesToUploadCount
          }, isOnline);

          setCopyAsset(asset);
        },
        icon: <CopyIcon />,
      },
      {
        label: "Move",
        key: "move",
        onClick: () => {
          const { id } = asset

          logUserEvent({
            user: email,
            eventType: `Move asset ${id} clicked on asset listing via Space Details view`,
            imageOutboxSize: imagesToUploadCount
          }, isOnline);

          setMovingAsset(asset);
        },
        icon: <MoveIcon />,
      },
      {
        label: "Delete",
        key: "delete",
        onClick: () => {
          const { id } = asset

          logUserEvent({
            user: email,
            eventType: `Delete asset ${id} clicked on asset listing via Space Details view`,
            imageOutboxSize: imagesToUploadCount
          }, isOnline)
          confirmDeleteAsset(asset)
        },
        icon: <DeleteIcon />,
      },
    ];
  };

  const filterTreeData = useMemo(() => {
    // search through facets.json and find appropriate facets
    const filterableFacets = structuredClone(window.facets.filter((i) => i.filterable));

    const validationStatusFilterIndex = filterableFacets.findIndex(f => f.facetId === 'uf46');

    if (showAssetValidationStatus && validationStatusFilterIndex > -1) {
      const enhancedValidationStatus = getEnhancedValidationStatus(filterableFacets[validationStatusFilterIndex]);

      filterableFacets[validationStatusFilterIndex] = { ...enhancedValidationStatus };
    } else {
      // remove Validation Status from filters
      filterableFacets.splice(validationStatusFilterIndex, 1);
    }

    const nonRatingGroupFacets = filterableFacets.filter((i) => !i.ratingGroup);
    const ratingGroupFacets = filterableFacets.filter((i) => i.ratingGroup);

    const nonRGF = nonRatingGroupFacets.map((i) => {
      return buildFilterableTreeByFacet(
        assets,
        i,
        onFiltersChange,
        filters,
        onFilterClose
      );
    });

    const uniqueRGs = _.uniq(ratingGroupFacets.map((f) => f.ratingGroup));

    const rgTrees = uniqueRGs
      .map((rg) =>
        treeObject(
          rg,
          rg,
          ratingGroupFacets
            .filter((rgf) => rgf.ratingGroup === rg)
            .map((i) => {
              return buildFilterableTreeByFacet(
                assets,
                i,
                onFiltersChange,
                filters,
                onFilterClose
              );
            })
            .filter((f) => f)
        )
      )
      .filter((rg) => rg.children && rg.children.length > 0);

    const RGF =
      rgTrees.length > 0 ? [treeObject("Rating", "Rating", rgTrees)] : [];

    // build System filter
    const treeData = buildFilterTreeFromHierarchy(
      window.hierarchy,
      assets,
    );
    const SF = treeObject("System", "System", treeData);

    // build Asset Class filter
    const ACF = treeObject(
      "Asset Class",
      "Asset Class",
      filterableOptions(assets, { key: "Asset Class" })
    );

    return [SF, ACF, ...nonRGF, ...RGF];
  }, [assets, filters]);
  
  if (!space || !assets || !floor || !facility) {
    return <Spinner message="We are fetching and processing your data..." />;
  }

  async function confirmDeleteAsset(asset) {
    const modal = Modal.confirm({
      className: "delete-confirmation-modal",
      title: `Delete Asset - ${asset.name}?`,
      content:  
        <>
          <p>Are you sure you want to delete <span className="delete-confirmation-assertive-text">{asset.name}{asset?.notes?.description ? ' - ' + asset.notes.description : null}</span>?</p>
          <Checkbox
            className="delete-confirmation-checkbox"
            onChange={(event) => onConfirmDelete(event.target.checked, modal)}
          >
            <span className="delete-confirmation-assertive-text">Yes, remove asset</span>
          </Checkbox>
        </>,
      icon: <WarningOutlined className="delete-confirmation-warning-icon"/>,
      okText: "Confirm",
      cancelText: "Cancel",
      width: 600,
      okButtonProps: {
        disabled: true
      },
      onOk: async () => {
        const itemToDelete = await DataStore.query(Asset, asset.id);

        await DataStore.delete(itemToDelete);

        logUserEvent({
          user: email,
          eventType: `Asset deleted ${asset.id} via Delete Asset modal on Space Details view`,
          imageOutboxSize: imagesToUploadCount
        }, isOnline);
      },
    });
  }

  const onConfirmDelete = (checked, modal) => {
    modal.update((prevConfig) => ({
      ...prevConfig,
      okButtonProps: {
        disabled: !checked
      }
    }));
  }

  async function confirmDeleteSpace(space) {
    const assetCount = Object.values(assetData?.items)?.filter(a => a.spaceId === space.id)?.length || null

    const modal = Modal.confirm({
      className: "delete-confirmation-modal",
      title: `Delete Space - ${space.name}`,
      content:  
        <>
          <p>
            Are you sure you want to delete <span className="delete-confirmation-assertive-text">{space.name}</span>?<br/>
            {assetCount === 1 ? 'Then ' : 'All '}
            <span className="delete-confirmation-assertive-text">{assetCount} asset{assetCount !== 1 ? 's' : null}</span> belonging to this space will also be deleted.
          </p>
          <Checkbox
            className="delete-confirmation-checkbox"
            onChange={(event) => onConfirmDelete(event.target.checked, modal)}>
            <span className="delete-confirmation-assertive-text">Yes, remove space and assets</span>
          </Checkbox>
        </>,
      icon: <WarningOutlined className="delete-confirmation-warning-icon" />,
      okText: "Confirm",
      cancelText: "Cancel",
      width: 600,
      okButtonProps: {
        disabled: true
      },
      onOk: async () => {
        // Delete Assets in space before deleting Space
        const assetsToDelete = await DataStore.query(Asset, (x) =>
          x.spaceId("eq", space.id)
        );

        for (let i = 0; i < assetsToDelete.length; i++) {
          await DataStore.delete(assetsToDelete[i]);

          console.info(`Delete asset: ${assetsToDelete[i].name}`);
        }

        const spaceToDelete = await DataStore.query(Space, space.id);
        
        await DataStore.delete(spaceToDelete);

        setDeletedSpaces((deleted) => {
          return [...deleted, spaceToDelete.id]
        });

        logUserEvent({
          user: email,
          eventType: `Space deleted ${space.id} via Delete Space modal on Space Details view`,
          imageOutboxSize: imagesToUploadCount
        }, isOnline);

        navigate(`/floor/${space.floorId}`);
      },
    });
  }

  const columnTitles = {
    installDate: "Installed",
    barcode: "Asset Id",
    updatedAt: "Last Edited",
    quantity: 'QTY',
    validated: 'Status'
  };

  const tableColumnTitleProps = {
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    columnTitles,
  };

  const ASSET_COLUMNS = ASSET_COLUMN({
    showAssetValidationStatus,
    tableColumnTitleProps,
    sortBy,
    sortOrder,
    actions: (asset) => <HiddenActionsInDropdown actions={actions(asset)} />
  });

  return (
    <>
      <div className="space-details-page">
      <Helmet title={space?.name || "Space details"} />
      {pageHeader}
      <div className="content-header">
        <Typography.Title className="subtitle" level={1}>
          Space details - {space?.name}
        </Typography.Title>
        {copyAssetMessage && (
          <CopyAssetCompleteMessage
            copyAssetMessage={copyAssetMessage}
            setCopyAssetMessage={setCopyAssetMessage}
            space={space}
          />
        )}
        {movingAssetComplete && (
          <Alert
            message={`Asset ${movingAssetComplete.name} has been sucessfully moved`}
            description={`The asset ${movingAssetComplete.name} has been sucessfully moved to ${movingAssetComplete.space}`}
            type="success"
            showIcon
            afterClose={() => {
              setMovingAssetComplete();
            }}
            closable
          />
        )}

        <FilterHeader
          filterTreeData={filterTreeData}
          filterOnChange={(change) => {
            onFiltersChange(change);
          }}
          filterOnCloseTag={(closeTag) => onFilterClose(closeTag)}
          filterValue={filters}
          buttonPair={{
            label: "Add Asset",
            onClick: () => {
              logUserEvent({
                user: email,
                eventType: "Add Asset button clicked on Spaces Details view",
                imageOutboxSize: imagesToUploadCount
              }, isOnline);

              setIsCreatingNewAsset(true);
            },
            options: (
              <Menu>
                <Menu.Item
                  key="favourite"
                  onClick={() => {
                    logUserEvent({
                      user: email,
                      eventType: `Space ${space.id} added/removed from Favourites via Add Asset button on Space Details view`,
                      imageOutboxSize: imagesToUploadCount
                    }, isOnline);

                    saveFavouriteSpaces(
                      setFavouriteSpaces,
                      favouriteSpaces,
                      space.id
                    )
                  }}
                >
                  {favouriteSpaces.includes(space.id)
                    ? "Unfavourite"
                    : "Favourite"}
                </Menu.Item>
                <Menu.Item
                  key="copy-assets"
                  onClick={() => {
                    logUserEvent({
                      user: email,
                      eventType: "Copy assets (from space) clicked via Add Asset button on Space Details view",
                      imageOutboxSize: imagesToUploadCount
                    }, isOnline);

                    setAssetsToCopyList([])
                    setShowCopyFromSpaceDrawer(true)
                  }}
                >
                  Copy Assets
                </Menu.Item>
                <Menu.Item
                  key="edit"
                  onClick={() => {
                    logUserEvent({
                      user: email,
                      eventType: "Edit Space clicked via Add Asset button on Space Details view",
                      imageOutboxSize: imagesToUploadCount
                    }, isOnline);

                    setEditSpace(true)
                  }}
                >
                  Edit Space
                </Menu.Item>
                <Menu.Item
                  key="delete"
                  onClick={() => confirmDeleteSpace(space)}
                >
                  Delete Space
                </Menu.Item>
              </Menu>
            ),
          }}
          sortByOptions={ASSET_COLUMNS}
          {...tableColumnTitleProps}
        />
        <CopyAssetPane
          ASSET_COLUMNS={ASSET_COLUMNS}
          setCopyAsset={setCopyAsset}
          setCopyAssetMessage={setCopyAssetMessage}
        />
      </div>
      <Authorize allowedRoles={["fabric"]}>
        <FabricGroups
          parentType="space"
          parents={{ facility, floor, space }}
          fabricAssets={fabricAssets}
        />
      </Authorize>

      <Authorize allowedRoles={["surveyor", "admin", "supplier"]}>
        <ItemTableGrid
          title="Assets"
          items={enhancedAssets}
          columns={ASSET_COLUMNS}
          onClick={(a) => setSelectedAsset(a)}
          actions={actions}
          render={useRenderGrid}
        />
      </Authorize>

      {(selectedAsset || isCreatingNewAsset || copyAsset) && (
        <ItemDrawer
          onClose={(saveButton = false, x) => {
            if (copyList.includes(copyAsset?.id) && saveButton && copyAsset) {
              if (copyList.length === 1) setCopyAssetMessage(copyList[0]);

              setCopyList(copyList.filter((x) => x !== copyAsset.id));
            }
            setCopyAsset();
            setIsCreatingNewAsset(false);
            setSelectedAsset();
            if (x) {
              setCopyAsset(x);
            }
          }}
          visible={selectedAsset || isCreatingNewAsset || copyAsset}
          item={selectedAsset}
          copy={copyAsset}
          itemType="asset"
          parents={{ space, floor, facility }}
        />
      )}
      {movingAsset && (
        <MoveAssetDrawer
          onClose={() => {
            setMovingAsset(false);
          }}
          complete={setMovingAssetComplete}
          visible={movingAsset}
          asset={movingAsset}
          parents={{ space, floor, facility }}
        />
      )}
      {space && editSpace && (
        <ItemDrawer
          onClose={() => setEditSpace(false)}
          visible={space}
          item={space}
          parents={{ floor, facility }}
          itemType="space"
        />
      )}
      <CopyAssetDrawer
        open={showCopyDrawer}
        assets={assetsToCopyList}
        onClose={assets => {
          if (assets) {
            setCopyList(assets)
          }
          setShowCopyDrawer(false)}
        }
      />
      <CopyAssetsFromSpaceDrawer
        open={showCopyFromSpaceDrawer}
        currentSpace={space}
        onClose={assets => {
          if (assets) {
            setCopyList(assets)
          }

          setShowCopyFromSpaceDrawer(false)
        }}
      />
    </div>
    </>
  );
}
